import classnames from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import * as MediaQueries from 'constants/mediaQueries';

const Cell = ({
  children,
  small,
  smallUp,
  medium,
  mediumUp,
  large,
  largeUp,
  extraLarge,
  className,
  ...otherProps
}) => {
  /*
    DISCLAIMER:

    Duplicating our styles below is not ideal but styled-jsx does not
    allow for dynamic generation of classes. Media Queries cannot be
    inlined via a style="" HTML attribute which means we need to use
    classname-based styles vs. inlining them. While we could rely on
    global classes living in ./src/styles/theme/media-queries, this
    goes against our convention of requiring components to be able
    to live entirely on their own without any external styles.
  */

  const classes = classnames('Grid__cell', className, {
    [`Grid__cell--small-${small.columns}`]: get(small, ['columns'], false),
    [`Grid__cell--small-offset-left-${small.columns}`]: get(small, ['offset'], false),
    [`Grid__cell--small-hidden`]: get(small, ['isHidden'], false),
    [`Grid__cell--small-up-${smallUp.columns}`]: get(smallUp, ['columns'], false),
    [`Grid__cell--small-up-offset-left-${smallUp.offset}`]: get(smallUp, ['offset'], false),
    [`Grid__cell--small-up-hidden`]: get(smallUp, ['isHidden'], false),
    [`Grid__cell--medium-${medium.columns}`]: get(medium, ['columns'], false),
    [`Grid__cell--medium-offset-left-${medium.offset}`]: get(medium, ['offset'], false),
    [`Grid__cell--medium-hidden`]: get(medium, ['isHidden'], false),
    [`Grid__cell--medium-up-${mediumUp.columns}`]: get(mediumUp, ['columns'], false),
    [`Grid__cell--medium-up-offset-left-${mediumUp.offset}`]: get(mediumUp, ['offset'], false),
    [`Grid__cell--medium-up-hidden`]: get(mediumUp, ['isHidden'], false),
    [`Grid__cell--large-${large.columns}`]: get(large, ['columns'], false),
    [`Grid__cell--large-offset-left-${large.offset}`]: get(large, ['offset'], false),
    [`Grid__cell--large-hidden`]: get(large, ['isHidden'], false),
    [`Grid__cell--large-up-${largeUp.columns}`]: get(largeUp, ['columns'], false),
    [`Grid__cell--large-up-offset-left-${largeUp.offset}`]: get(largeUp, ['offset'], false),
    [`Grid__cell--large-up-hidden`]: get(largeUp, ['isHidden'], false),
    [`Grid__cell--extra-large-${extraLarge.columns}`]: get(extraLarge, ['columns'], false),
    [`Grid__cell--extra-large-offset-left-${extraLarge.offset}`]: get(
      extraLarge,
      ['offset'],
      false
    ),
    [`Grid__cell--extra-large--hidden`]: get(extraLarge, ['isHidden'], false),
  });

  return (
    <div className={classes} {...otherProps}>
      {children}

      <style jsx>
        {`
          @media ${MediaQueries.small} {
            .Grid.Grid--small-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.small} {
            .Grid.Grid--small-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.small} {
            .Grid.Grid--small-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.small} {
            .Grid.Grid--small-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.small} {
            .Grid__cell.Grid__cell--small-hidden {
              display: none;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid.Grid--small-up-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid.Grid--small-up-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid.Grid--small-up-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid.Grid--small-up-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.smallUp} {
            .Grid__cell.Grid__cell--small-up-hidden {
              display: none;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid.Grid--medium-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid.Grid--medium-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid.Grid--medium-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid.Grid--medium-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.medium} {
            .Grid__cell.Grid__cell--medium-hidden {
              display: none;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid.Grid--medium-up-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid.Grid--medium-up-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid.Grid--medium-up-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid.Grid--medium-up-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.mediumUp} {
            .Grid__cell.Grid__cell--medium-up-hidden {
              display: none;
            }
          }

          @media ${MediaQueries.large} {
            .Grid.Grid--large-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.large} {
            .Grid.Grid--large-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.large} {
            .Grid.Grid--large-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.large} {
            .Grid.Grid--large-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.large} {
            .Grid__cell.Grid__cell--large-hidden {
              display: none;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid.Grid--large-up-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid.Grid--large-up-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid.Grid--large-up-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid.Grid--large-up-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.largeUp} {
            .Grid__cell.Grid__cell--large-up-hidden {
              display: none;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid.Grid--extra-large-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid.Grid--extra-large-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid.Grid--extra-large-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid.Grid--extra-large-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.extraLarge} {
            .Grid__cell.Grid__cell--extra-large-hidden {
              display: none;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid.Grid--landscape-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid.Grid--landscape-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid.Grid--landscape-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid.Grid--landscape-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.landscape} {
            .Grid__cell.Grid__cell--landscape-hidden {
              display: none;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid.Grid--portrait-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid.Grid--portrait-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid.Grid--portrait-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid.Grid--portrait-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.portrait} {
            .Grid__cell.Grid__cell--portrait-hidden {
              display: none;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid.Grid--retina-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid.Grid--retina-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid.Grid--retina-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid.Grid--retina-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.retina} {
            .Grid__cell.Grid__cell--retina-hidden {
              display: none;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid.Grid--ie10-and-above-flex-direction-row {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid.Grid--ie10-and-above-flex-direction-row-reverse {
              flex-direction: row-reverse;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid.Grid--ie10-and-above-flex-direction-column {
              flex-direction: column;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid.Grid--ie10-and-above-flex-direction-column-reverse {
              flex-direction: column-reverse;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-1 {
              width: 8.33333%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-1 {
              margin-left: 8.33333%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-2 {
              width: 16.66667%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-2 {
              margin-left: 16.66667%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-3 {
              width: 25%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-3 {
              margin-left: 25%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-4 {
              width: 33.33333%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-4 {
              margin-left: 33.33333%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-5 {
              width: 41.66667%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-5 {
              margin-left: 41.66667%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-6 {
              width: 50%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-6 {
              margin-left: 50%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-7 {
              width: 58.33333%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-7 {
              margin-left: 58.33333%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-8 {
              width: 66.66667%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-8 {
              margin-left: 66.66667%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-9 {
              width: 75%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-9 {
              margin-left: 75%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-10 {
              width: 83.33333%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-10 {
              margin-left: 83.33333%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-11 {
              width: 91.66667%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-11 {
              margin-left: 91.66667%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-12 {
              width: 100%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-offset-left-12 {
              margin-left: 100%;
            }
          }

          @media ${MediaQueries.ie10AndAbove} {
            .Grid__cell.Grid__cell--ie10-and-above-hidden {
              display: none;
            }
          }

          .Container {
            width: 100%;
            margin: 0 auto;
            max-width: 1200px;
            padding: 0 24px;
          }

          .Grid {
            display: flex;
            width: 100%;
            flex-flow: row wrap;
            margin: 0 auto;
          }

          .Grid.Grid--centered {
            justify-content: center;
          }

          .Grid__cell {
            position: relative;
            width: 100%;
            display: block;
          }

          .Grid__cell-inner--vertically-centered {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
          }
        `}
      </style>
    </div>
  );
};

Cell.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  className: PropTypes.string,
  small: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      columns: PropTypes.number,
    }),
  ]),
  smallUp: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      columns: PropTypes.number,
    }),
  ]),
  medium: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      columns: PropTypes.number,
    }),
  ]),
  mediumUp: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      columns: PropTypes.number,
    }),
  ]),
  large: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      columns: PropTypes.number,
    }),
  ]),
  largeUp: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      columns: PropTypes.number,
    }),
  ]),
  extraLarge: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      columns: PropTypes.number,
    }),
  ]),
};

Cell.defaultProps = {
  small: false,
  smallUp: false,
  medium: false,
  mediumUp: false,
  large: false,
  largeUp: false,
  extraLarge: false,
  className: '',
};

export default Cell;
